import { useCallback, useState } from 'react';
import { getApiErrorMessage } from '../firebase/config.js';
import * as examsApi from '../lib/exams.js';

// Shared exam-creation logic for both orchestrators (AppHome + MainApp).
//
// The backend now owns generation, grading, and the credit debit (1 credit per produced
// question, atomic with generation). The client therefore NO LONGER pre-debits via
// /api/credits/debit for exams — a broke user is rejected by POST /api/exams with
// 402 INSUFFICIENT_CREDITS (details.required/available).
//
// This hook is UI-agnostic: callers inject `notify(kind, message)` and an optional
// `refreshEntitlements()` so each page keeps its own toast / notification style.
//
// Each create* returns the sanitized exam (with examId) on success, or null on handled
// failure (the caller typically does setActiveExam(exam) on a truthy result).
export function useExams({ notify, refreshEntitlements } = {}) {
    const [creating, setCreating] = useState(false);

    const announce = useCallback((kind, message) => {
        try { notify?.(kind, message); } catch (_) {}
    }, [notify]);

    const runCreate = useCallback(async (source, config) => {
        setCreating(true);
        try {
            const exam = await examsApi.createExam(source, config);
            announce('success', 'Exam created');
            return exam;
        } catch (e) {
            console.error('[useExams] createExam failed:', { code: e?.code, requestId: e?.requestId, status: e?.status });
            if (e?.code === 'INSUFFICIENT_CREDITS') {
                const required = e?.details?.required;
                const available = e?.details?.available;
                announce('error', (required != null && available != null)
                    ? `Not enough credits. Need ${required}, have ${available}.`
                    : 'Not enough credits.');
            } else {
                announce('error', getApiErrorMessage(e, 'Create exam failed'));
            }
            return null;
        } finally {
            // Credits changed (or were checked) — refresh the balance UI.
            if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) {} }
            setCreating(false);
        }
    }, [announce, refreshEntitlements]);

    const createFromCards = useCallback((cards, config) =>
        runCreate({ type: 'cards', cards }, config), [runCreate]);

    const createFromFolders = useCallback((folderIds, config) =>
        runCreate({ type: 'folders', folderIds }, config), [runCreate]);

    const createClinical = useCallback((text, clinical, config) =>
        runCreate({ type: 'clinical', text, clinical }, config), [runCreate]);

    return {
        creating,
        createFromCards,
        createFromFolders,
        createClinical,
        // Re-exported for attempt history / saved-exam listing (Phase 4).
        listExams: examsApi.listExams,
        listAttempts: examsApi.listAttempts,
    };
}

export default useExams;
